import { t } from '@lingui/core/macro'
import type { FimSubjectRecord, Reference } from 'features/fim'
import { parseBboxStrings } from 'features/fimEvidence/types'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'

/** One referenced document, aggregated across a Subject's citations. */
export interface SubjectCitation {
  documentId: string
  label: string
  pages: number[]
  reason?: string
  /** First cited region for this document, for a region-framed open. */
  region?: { page: number; boxes: NormalizedBBox[] }
}

/** Dossier payloads are untyped `Record<string, unknown>`; nesting is bounded
 * so a deep or cyclic-looking payload cannot stall the render. */
const MAX_DOSSIER_DEPTH = 4

const isReferenceLike = (value: unknown): value is Reference =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { document_id?: unknown }).document_id === 'string'

const dossierReferences = (
  dossier: Record<string, unknown> | null | undefined
): Reference[] => {
  if (!dossier) return []
  const found: Reference[] = []
  const visit = (value: unknown, depth: number) => {
    if (depth > MAX_DOSSIER_DEPTH) return
    if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1)
      return
    }
    if (typeof value !== 'object' || value === null) return
    if (isReferenceLike(value)) {
      found.push(value)
      return
    }
    for (const nested of Object.values(value)) visit(nested, depth + 1)
  }
  visit(dossier, 0)
  return found
}

/**
 * Every document a Subject cites, grouped by document id: alias references
 * first (the fusion projection's own evidence), then any reference-shaped
 * entries carried in its dossier payload.
 */
export const collectSubjectCitations = (
  subject: FimSubjectRecord,
  resolveDocumentTitle?: (documentId: string) => string | undefined
): SubjectCitation[] => {
  const byId = new Map<string, SubjectCitation>()
  const references: Reference[] = [
    ...(subject.data.aliases ?? []).flatMap((alias) => alias.references ?? []),
    ...dossierReferences(subject.data.dossier),
  ]

  for (const reference of references) {
    const documentId = reference.document_id?.trim()
    if (!documentId) continue
    const page = typeof reference.page === 'number' ? reference.page : undefined
    const boxes = page === undefined ? [] : parseBboxStrings(reference.boxes)
    const existing = byId.get(documentId)
    if (!existing) {
      byId.set(documentId, {
        documentId,
        label:
          resolveDocumentTitle?.(documentId) ??
          (reference.locator?.trim() ||
            reference.ref ||
            t`Referenced document`),
        pages: page === undefined ? [] : [page],
        reason: reference.reason ?? undefined,
        ...(page !== undefined &&
          boxes.length > 0 && { region: { page, boxes } }),
      })
      continue
    }
    if (page !== undefined && !existing.pages.includes(page)) {
      existing.pages.push(page)
    }
    if (!existing.reason && reference.reason) existing.reason = reference.reason
    if (!existing.region && page !== undefined && boxes.length > 0) {
      existing.region = { page, boxes }
    }
  }

  return [...byId.values()].map((citation) => ({
    ...citation,
    pages: [...citation.pages].sort((left, right) => left - right),
  }))
}
